import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Chat.css';
import { renderBold } from '../utils/markdownText';

// Convierte un texto con citas a "Artículo N de la LEY" en JSX con enlaces.
// Detecta patrones tipo:  Artículo 14 de la Constitución Española
//                         art. 14 CE
//                         artículo 478 LOPJ
//                         artículo 134.4 de la Constitución Española (con apartado)
//                         artículo 8.3 de la Ley 40/2015 (ley citada por numero, sin acronimo)
// El ".N" del apartado se consume pero no se captura: el enlace apunta al
// articulo base ("Artículo 134"), que es como esta guardado en el grafo (el
// texto de cada apartado vive dentro del mismo nodo de articulo, no hay un
// nodo separado por apartado) -- enlazar al "134.4" literal no encontraria
// nada en /api/article_content.
//
// El nombre de la ley se captura con una palabra inicial + como mucho UNA
// palabra de continuacion de una lista cerrada ("Española", "Organica"...) +
// opcionalmente un sufijo "NN/AAAA", en vez de un char-class generico con
// cuantificador perezoso + lookahead: ese diseño anterior cortaba en el
// primer espacio interno, asi que "Ley 40/2015" quedaba capturado como
// solo "Ley" (sin el numero) y la busqueda del articulo fallaba siempre
// porque en el grafo la ley esta guardada como "Ley 40/2015" completo.
// No se puede usar un char-class basado en mayusculas para distinguir
// "Ley"/"Organica" (nombre de ley) de "para"/"segun" (prosa) porque el flag
// /i de toda la regex ya vuelve insensible a mayusculas cualquier char-class
// A-Z; de ahi la lista cerrada de continuaciones conocidas en su lugar.
const LAW_CONTINUATION_WORDS = 'Española|Org[aá]nica|Civil|Penal|Criminal|Procesal|General|Reguladora';
const ART_REGEX = new RegExp(
  String.raw`(?:art[íi]culo|art\.?)\s+(\d+(?:\s*(?:bis|ter|quater))?)(?:\.\d+)?\s+(?:de\s+la\s+)?` +
  String.raw`([A-ZÁÉÍÓÚÑa-záéíóúñ]+(?:\s+(?:${LAW_CONTINUATION_WORDS}))?(?:\s+\d+/\d{4})?)`,
  'gi'
);
// Mapa de acrónimos/nombres a la versión canónica en el grafo
const LAW_ALIASES = {
  'ce': 'Constitución Española',
  'constitución española': 'Constitución Española',
  'constitucion española': 'Constitución Española',
  'constitución': 'Constitución Española',
  'lopj': 'LOPJ',
  'lec': 'LEC',
  'lecrim': 'Enjuiciamiento Criminal',
  'lecr': 'Enjuiciamiento Criminal',
  'ley de enjuiciamiento criminal': 'Enjuiciamiento Criminal',
  'enjuiciamiento criminal': 'Enjuiciamiento Criminal',
  'ley de enjuiciamiento civil': 'LEC',
  'ljca': 'Ley 29/1998',
  'ljs': 'Ley 36/2011',
  'ebep': 'Real Decreto Legislativo 5/2015',
};

function canonicalLaw(name) {
  if (!name) return null;
  const cleaned = name.trim().toLowerCase().replace(/\s+/g, ' ');
  if (LAW_ALIASES[cleaned]) return LAW_ALIASES[cleaned];
  // Buscar key parcial
  for (const [k, v] of Object.entries(LAW_ALIASES)) {
    if (cleaned.includes(k)) return v;
  }
  return name.trim();
}

function renderWithLinks(text) {
  if (!text) return text;
  const parts = [];
  let lastIdx = 0;
  let match;
  ART_REGEX.lastIndex = 0;
  while ((match = ART_REGEX.exec(text)) !== null) {
    const [full, num, lawRaw] = match;
    const law = canonicalLaw(lawRaw);
    if (match.index > lastIdx) parts.push(text.slice(lastIdx, match.index));
    parts.push(
      <Link
        key={`${match.index}`}
        to={`/article/${encodeURIComponent(law)}/${encodeURIComponent(`Artículo ${num}`)}`}
        className="cite-link"
      >
        {full}
      </Link>
    );
    lastIdx = match.index + full.length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  const withLinks = parts.length ? parts : [text];
  // Los Link ya son JSX y se dejan tal cual; solo los trozos de texto plano
  // pueden llevar **negrita** del backend (prompts, mensaje de fallback).
  return withLinks.flatMap((part, i) =>
    typeof part === 'string' ? renderBold(part, `b${i}`) : part
  );
}

const Chat = () => {
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // 'auto' (sin animacion) mientras el ultimo mensaje se esta rellenando
    // token a token -- con 'smooth' el scroll se relanzaba en cada fragmento
    // recibido y quedaba con tirones.
    const last = conversation[conversation.length - 1];
    const behavior = last && last.streaming ? 'auto' : 'smooth';
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [conversation, isLoading]);

  const ERROR_MESSAGE = 'Lo siento, hubo un error al obtener la respuesta. Inténtalo de nuevo.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || isLoading) return;

    const userQuestion = question;
    setQuestion('');
    setIsLoading(true);

    // El mensaje del asistente se añade vacío desde ya y se va rellenando
    // con cada evento "token" del stream (ver render: mientras content==''
    // y streaming==true se muestran los puntos de carga en su propia burbuja).
    setConversation((prev) => [
      ...prev,
      { role: 'user', content: userQuestion },
      { role: 'assistant', content: '', qaId: null, fromCache: false, feedback: null, streaming: true },
    ]);

    const updateLastAssistant = (updater) => {
      setConversation((prev) => {
        const next = [...prev];
        const lastIdx = next.length - 1;
        next[lastIdx] = updater(next[lastIdx]);
        return next;
      });
    };

    try {
      const res = await fetch(`${axios.defaults.baseURL}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: userQuestion }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const rawEvents = buffer.split('\n\n');
        buffer = rawEvents.pop(); // el ultimo trozo puede venir incompleto

        for (const rawEvent of rawEvents) {
          const line = rawEvent.trim();
          if (!line.startsWith('data:')) continue;
          const payload = JSON.parse(line.slice(5).trim());

          if (payload.type === 'token') {
            updateLastAssistant((m) => ({ ...m, content: m.content + payload.text }));
          } else if (payload.type === 'done') {
            updateLastAssistant((m) => ({
              ...m,
              qaId: payload.qa_id || null,
              fromCache: !!payload.from_cache,
              streaming: false,
            }));
          } else if (payload.type === 'error') {
            updateLastAssistant((m) => ({
              ...m,
              content: m.content || ERROR_MESSAGE,
              streaming: false,
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching chat response', error);
      updateLastAssistant((m) => ({
        ...m,
        content: m.content || ERROR_MESSAGE,
        streaming: false,
      }));
    }
    setIsLoading(false);
  };

  const sendFeedback = async (idx, vote) => {
    const msg = conversation[idx];
    if (!msg.qaId || msg.feedback) return; // sin qaId o ya votado
    // optimistic UI
    setConversation((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, feedback: vote } : m))
    );
    try {
      await axios.post('/chat/feedback', { qa_id: msg.qaId, vote });
    } catch (err) {
      console.error('Feedback error', err);
      setConversation((prev) =>
        prev.map((m, i) => (i === idx ? { ...m, feedback: null } : m))
      );
    }
  };

  return (
    <div className="chat-page">
      <header className="page-header">
        <h1 className="page-title">Asistente Almaley</h1>
        <p className="page-subtitle">
          Pregunta sobre cualquier tema, ley o artículo de tu oposición.
        </p>
      </header>

      <div className="chat-container">
        <div className="chat-messages">
          {conversation.length === 0 && (
            <div className="chat-empty">
              <div className="chat-empty-icon" aria-hidden="true">💬</div>
              <p>Empieza una conversación. Por ejemplo:</p>
              <ul className="chat-suggestions">
                <li onClick={() => setQuestion('¿Qué establece el artículo 14 de la Constitución?')}>
                  ¿Qué establece el artículo 14 de la Constitución?
                </li>
                <li onClick={() => setQuestion('¿Cuáles son las funciones del Cuerpo de Auxilio Judicial?')}>
                  ¿Cuáles son las funciones del Cuerpo de Auxilio Judicial?
                </li>
                <li onClick={() => setQuestion('Explícame los recursos de la LEC')}>
                  Explícame los recursos de la LEC
                </li>
              </ul>
            </div>
          )}

          {conversation.map((msg, index) => {
            const isEmptyStreamingBubble = msg.role === 'assistant' && msg.streaming && !msg.content;
            return (
            <div key={index} className={`message message-${msg.role}`}>
              <div className={`message-bubble${isEmptyStreamingBubble ? ' loading' : ''}`}>
                {isEmptyStreamingBubble ? (
                  <>
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </>
                ) : (
                  msg.role === 'assistant' ? renderWithLinks(msg.content) : msg.content
                )}
                {msg.role === 'assistant' && msg.fromCache && (
                  <span className="cache-badge" title="Respuesta servida desde la caché">⚡ caché</span>
                )}
              </div>

              {msg.role === 'assistant' && msg.qaId && (
                <div className="feedback-row">
                  <button
                    className={`feedback-btn ${msg.feedback === 'up' ? 'active' : ''}`}
                    onClick={() => sendFeedback(index, 'up')}
                    disabled={!!msg.feedback}
                    aria-label="Útil"
                  >
                    👍
                  </button>
                  <button
                    className={`feedback-btn ${msg.feedback === 'down' ? 'active' : ''}`}
                    onClick={() => sendFeedback(index, 'down')}
                    disabled={!!msg.feedback}
                    aria-label="No útil"
                  >
                    👎
                  </button>
                  {msg.feedback && (
                    <span className="feedback-thanks">¡Gracias!</span>
                  )}
                </div>
              )}
            </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="chat-form">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Escribe tu pregunta..."
            className="chat-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="btn btn-primary chat-send"
            disabled={isLoading || !question.trim()}
          >
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
